import { useCallback, useEffect, useState } from "react"
import { useCallSheetData } from "./useCallSheetData"
import { NON_BILLABLE_OPTIONS } from "../constants"
import { useCallFormState } from "./useCallFormState"
import useHospitalData from "hooks/useHospitalData"

export const useCallFormFields = (answeredCall, setServerState) => {
  const [callerAccessCodeId, setCallerAccessCodeId] = useState(null)
  const [meetingUrl, setMeetingUrl] = useState(null)
  const [showCollectLocation, setShowCollectLocation] = useState(false)
  const [showCollectDepartment, setShowCollectDepartment] = useState(false)
  const originalPartnerId = answeredCall?.interaction?.originalClientId ?? null
  const { callSheetData, getCallSheetData } = useCallSheetData(answeredCall?.interaction?.id)
  const interactionPartnerId = answeredCall?.interaction?.hospitalId ?? callSheetData?.partnerId ?? null
  const { hospital: originalPartner } = useHospitalData(originalPartnerId ?? null)

  const { reset, onCallSheetLoad, ...rest } = useCallFormState()

  const { setInteractionId, setInteractionName, setUnitId, setCustomerPhoneNumber, setLanguage } = rest

  const refreshCallSheet = useCallback(() => {
    return getCallSheetData()
  }, [getCallSheetData])

  useEffect(() => {
    if (!(answeredCall?.interaction || answeredCall?.language)) {
      return
    }
    /* NOTE:
      This assumes that neither the interaction nor language objects ever change once set.
      Since some of this data can come from the interaction object instead, they will be nullified if answeredCall updates.
    */
    const { id, name, unitId, customerPhoneNumber } = answeredCall.interaction

    setInteractionId(id)
    setInteractionName(name)
    setUnitId(unitId || "")
    setCustomerPhoneNumber(customerPhoneNumber)
    setLanguage(answeredCall.language?.name)
    return () => {
      reset()
    }
  }, [
    answeredCall?.interaction,
    answeredCall?.language,
    reset,
    setCustomerPhoneNumber,
    setInteractionId,
    setInteractionName,
    setLanguage,
    setUnitId,
  ])

  useEffect(() => {
    if (callSheetData) {
      const {
        partnerId,
        departmentId,
        providerFirstName,
        providerLastName,
        patientFirstName,
        patientLastName,
        patientMRN,
        patientDOB,
        nonBillableReason,
        phoneNumberAC,
        bchOaklandHospitalConfirmation,
        location,
        memberIdNumber,
        nyuAccessCode,
        sentaraAccessCode,
        visitId,
        callerFirstName,
        callerLastName,
        meetingUrl,
        showCollectLocation: collectLocation,
        showCollectDepartment: collectDepartment,
      } = callSheetData
      setCallerAccessCodeId(callSheetData.accessCodeId)
      setShowCollectLocation(collectLocation ?? false)
      setShowCollectDepartment(collectDepartment ?? false)

      const treeParentId = originalPartnerId ?? partnerId
      const partnerIdForDropdown = partnerId === treeParentId ? "" : (partnerId || "")

      onCallSheetLoad({ ...callSheetData, partnerId: partnerIdForDropdown })
      setMeetingUrl(meetingUrl)
      setServerState({
        partnerId: partnerIdForDropdown,
        nonBillableReason: nonBillableReason === NON_BILLABLE_OPTIONS.NONE.key ? null : nonBillableReason,
        departmentId: departmentId || "",
        patientFirstName: patientFirstName || "",
        patientLastName: patientLastName || "",
        patientMRN: patientMRN,
        patientDOB: patientDOB || null,
        providerFirstName: providerFirstName || "",
        providerLastName: providerLastName || "",
        phoneNumberAC: phoneNumberAC || "",
        bchOaklandHospitalConfirmation: bchOaklandHospitalConfirmation || false,
        location: location || "",
        memberIdNumber: memberIdNumber || "",
        nyuAccessCode: nyuAccessCode || "",
        sentaraAccessCode: sentaraAccessCode || "",
        visitId: visitId || "",
        callerFirstName: callerFirstName || "",
        callerLastName: callerLastName || "",
      })
    }
  }, [callSheetData, onCallSheetLoad, originalPartnerId, setServerState])

  useEffect(() => {
    if (!answeredCall) {
      setCallerAccessCodeId(null)
      setMeetingUrl(null)
      setShowCollectLocation(false)
      setShowCollectDepartment(false)
      setInteractionId("")
      setInteractionName("")
      setUnitId("")
      setCustomerPhoneNumber("")
      setLanguage("")
      return
    }
  }, [
    answeredCall,
    setInteractionId,
    setInteractionName,
    setUnitId,
    setCustomerPhoneNumber,
    setLanguage,
  ])

  return {
    ...rest,
    interactionPartnerId,
    originalPartnerId,
    originalPartnerName: originalPartner?.clientName ?? callSheetData?.partnerFriendlyName ?? "",
    refreshCallSheet,
    callerAccessCodeId,
    showCollectLocation,
    showCollectDepartment,
    meetingUrl,
  }
}
